import { Unlink } from 'lucide-react';
import * as React from 'react';

import { LinkSessionsComboBoxResponsive } from '@/components/ui/responsive-combobox/link-sessions-responsive-combobox';
import { useIsFindOrLinkPatientEnabled } from '@/features/flags';
import { SessionListLinkPopover } from '@/features/patient-find-or-link/components/session-list-link-popover';
import { cn } from '@/lib/cn';
import { Avatar, AvatarFallback } from '@heidi/design-system/avatar';
import type { PatientProfileAppointmentCandidates } from '@heidi/session/contracts';
import { useIntl } from 'react-intl';
import { consolidatedSessionsListMessages } from '../consolidatedSessionsList.messages';

interface ConsolidatedSessionLinkAvatarProps {
  /** Session id from the consolidated row. Falls through to a static
   *  avatar if missing (degraded session shape). */
  sessionId?: string | null;
  /** Linked profile flag — toggles initials vs Unlink icon and disables
   *  the popover (already-linked rows can't be re-linked here). */
  isSessionLinked: boolean;
  /** Initials to display when linked. */
  patientInitials?: string;
  /** Suggested match candidates returned on the session-list response. */
  patientProfileAppointmentCandidates?: PatientProfileAppointmentCandidates | null;
  /** Synthetic pre-sync offline rows carry a local id and no backend
   *  session, so the patient-link popover must be disabled — sending the
   *  local id to a patient-link mutation would target a non-existent
   *  session. Mirrors the new session list's synthetic-row gating. */
  isOfflineSynthetic?: boolean;
}

/**
 * The patient-profile avatar in the consolidated sessions list. When
 * the session is unlinked, clicking opens a popover to link a patient.
 *
 * Mirrors the in-session combobox simplicity: gates only on
 * `isSessionLinked`. Permission and EHR-readonly checks happen on the
 * mutation server-side; the UI surfaces the affordance uniformly so
 * users get a consistent experience between the in-session header and
 * the session list.
 *
 * Behind `useIsFindOrLinkPatientEnabled()` the popover uses our
 * find-or-link search; otherwise it falls back to the legacy
 * `LinkSessionsComboBoxResponsive` so users without the flag still get
 * the prod link affordance.
 */
export const ConsolidatedSessionLinkAvatar: React.FC<
  ConsolidatedSessionLinkAvatarProps
> = ({
  sessionId,
  isSessionLinked,
  patientInitials,
  patientProfileAppointmentCandidates,
  isOfflineSynthetic = false,
}) => {
  const intl = useIntl();
  const isFindOrLinkEnabled = useIsFindOrLinkPatientEnabled();
  const linkPatientDisabled = isSessionLinked || isOfflineSynthetic;

  const avatar = (
    <Avatar
      className={cn(
        'size-5 shrink-0 border-[0.75px]',
        isSessionLinked
          ? 'border-border-selected text-text-primary'
          : 'border-text-tertiary text-text-tertiary',
        !linkPatientDisabled && 'hover:border-border-selected hover:text-text-primary'
      )}
    >
      <AvatarFallback
        className={cn(
          'flex items-center justify-center bg-transparent text-[10px]',
          !isSessionLinked && 'p-1'
        )}
      >
        {isSessionLinked ? patientInitials : <Unlink size={10} />}
      </AvatarFallback>
    </Avatar>
  );

  // Already-linked or session-id-less rows render the avatar as a
  // static decoration. Otherwise we render the popover wrapper.
  if (!sessionId || linkPatientDisabled) return avatar;

  const linkComboboxProps = {
    trigger: avatar,
    tooltipContent: intl.formatMessage(
      consolidatedSessionsListMessages.linkSessionToPatient
    ),
    popoverAlign: 'start' as const,
    popoverSide: 'right' as const,
    popoverAlignOffset: -2,
    popoverSideOffset: 4,
    commandInputPlaceholder: intl.formatMessage(
      consolidatedSessionsListMessages.searchForPatient
    ),
    selectedSessionIds: [sessionId],
    showTooltip: !isSessionLinked,
    disabled: linkPatientDisabled,
    patientProfileAppointmentCandidates,
  };

  // Wrapper div eats the click/keydown bubble so the row's
  // `onSessionClick` doesn't fire when the user clicks the avatar.
  // Stop fires AFTER the popover trigger's own handler thanks to
  // bubbling order — popover opens first, then bubble dies here
  // before reaching the row-level link.
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation();
        }
      }}
    >
      {isFindOrLinkEnabled ? (
        <SessionListLinkPopover {...linkComboboxProps} />
      ) : (
        <LinkSessionsComboBoxResponsive {...linkComboboxProps} />
      )}
    </div>
  );
};
